import React, { useState, useEffect, useCallback } from 'react';
import api from '../../utils/api';

const STATUS_LABEL = {
  confirmed: 'Confirmada', attended: 'Atendida',
  not_attended: 'No asistió', cancelled: 'Cancelada', rescheduled: 'Reagendada',
};
const STATUS_COLOR = {
  confirmed: '#2563eb', attended: '#16a34a',
  not_attended: '#64748b', cancelled: '#dc2626', rescheduled: '#8b5cf6',
};

const CHIPS = [
  ['all', 'Todas'],
  ['confirmed', 'Confirmadas'],
  ['attended', 'Atendidas'],
  ['not_attended', 'No asistió'],
  ['cancelled', 'Canceladas'],
];

const AVATAR_COLORS = ['#2563eb', '#16a34a', '#d97706', '#8b5cf6', '#db2777', '#0891b2'];
const initials = name => (name || '?').trim().split(/\s+/).slice(0, 2).map(p => p[0]?.toUpperCase()).join('');
const avatarColor = name => AVATAR_COLORS[((name || '').length) % AVATAR_COLORS.length];
const toDateStr = d => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;

export default function BarberAppointmentsPage() {
  const [monthAppts, setMonthAppts] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(toDateStr(new Date()));
  const [calYear, setCalYear] = useState(new Date().getFullYear());
  const [calMonth, setCalMonth] = useState(new Date().getMonth());
  const [statusFilter, setStatusFilter] = useState('all');
  const [showCal, setShowCal] = useState(false);

  const [detail, setDetail] = useState(null);       // cita seleccionada (modal)
  const [updating, setUpdating] = useState(false);
  const [cancelMode, setCancelMode] = useState(false);
  const [cancelReason, setCancelReason] = useState('');

  const todayStr = toDateStr(new Date());

  const loadMonth = useCallback(async (year, month) => {
    setLoading(true);
    try {
      const daysInMonth = new Date(year, month + 1, 0).getDate();
      const start = `${year}-${String(month + 1).padStart(2, '0')}-01`;
      const end = `${year}-${String(month + 1).padStart(2, '0')}-${String(daysInMonth).padStart(2, '0')}`;
      const res = await api.get('/appointments/range', { params: { start, end } });
      const byDate = {};
      (res.data || []).forEach(a => {
        const d = a.appointment_date?.slice(0, 10);
        if (!d) return;
        if (!byDate[d]) byDate[d] = [];
        byDate[d].push(a);
      });
      setMonthAppts(byDate);
    } catch { setMonthAppts({}); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadMonth(calYear, calMonth); }, [calYear, calMonth, loadMonth]);

  const changeStatus = async (id, status, reason = '') => {
    setUpdating(true);
    try {
      await api.patch(`/appointments/${id}/status`, { status, cancellation_reason: reason });
      await loadMonth(calYear, calMonth);
      setDetail(null); setCancelMode(false); setCancelReason('');
    } catch {} finally { setUpdating(false); }
  };

  const goMonth = dir => {
    let y = calYear, m = calMonth + dir;
    if (m < 0) { y--; m = 11; } else if (m > 11) { y++; m = 0; }
    setCalYear(y); setCalMonth(m);
  };
  const shiftDay = dir => {
    const d = new Date(selectedDate + 'T12:00:00');
    d.setDate(d.getDate() + dir);
    setSelectedDate(toDateStr(d));
    setCalYear(d.getFullYear()); setCalMonth(d.getMonth());
  };

  const allDay = (monthAppts[selectedDate] || []).slice().sort((a, b) => (a.start_time || '').localeCompare(b.start_time || ''));
  const dayAppts = statusFilter === 'all' ? allDay : allDay.filter(a => a.status === statusFilter);

  const calDays = (() => {
    const first = new Date(calYear, calMonth, 1).getDay();
    const total = new Date(calYear, calMonth + 1, 0).getDate();
    const arr = [];
    for (let i = 0; i < first; i++) arr.push(null);
    for (let i = 1; i <= total; i++) arr.push(i);
    return arr;
  })();

  const summary = {
    total: allDay.length,
    confirmed: allDay.filter(a => a.status === 'confirmed').length,
    attended: allDay.filter(a => a.status === 'attended').length,
    cancelled: allDay.filter(a => a.status === 'cancelled').length,
  };
  const nextAppt = allDay.find(a => a.status === 'confirmed');
  const fmtLongDate = ds => new Date(ds + 'T12:00:00').toLocaleDateString('es-CO', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

  const MiniCalendar = () => (
    <div className="card">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <button className="btn btn-ghost btn-icon" onClick={() => goMonth(-1)}>‹</button>
        <span style={{ fontWeight: 700, fontSize: '0.9rem', textTransform: 'capitalize' }}>
          {new Date(calYear, calMonth).toLocaleDateString('es-CO', { month: 'long', year: 'numeric' })}
        </span>
        <button className="btn btn-ghost btn-icon" onClick={() => goMonth(1)}>›</button>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: 2 }}>
        {['D','L','M','X','J','V','S'].map(d => (
          <div key={d} style={{ textAlign: 'center', fontSize: '0.68rem', fontWeight: 700, color: 'var(--color-text-faint)', padding: '2px 0' }}>{d}</div>
        ))}
        {calDays.map((d, i) => {
          if (!d) return <div key={i} />;
          const ds = `${calYear}-${String(calMonth+1).padStart(2,'0')}-${String(d).padStart(2,'0')}`;
          const has = (monthAppts[ds] || []).length > 0;
          const isToday = ds === todayStr;
          const isSel = ds === selectedDate;
          return (
            <div key={i} onClick={() => { setSelectedDate(ds); setShowCal(false); }} style={{
              aspectRatio: '1', display: 'flex', alignItems: 'center', justifyContent: 'center',
              borderRadius: 8, cursor: 'pointer', fontSize: '0.8rem', position: 'relative',
              fontWeight: isToday || isSel ? 700 : 500,
              background: isSel ? 'var(--color-info)' : isToday ? 'var(--color-info-bg)' : 'transparent',
              color: isSel ? '#fff' : isToday ? 'var(--color-info)' : 'var(--color-text)',
            }}>
              {d}
              {has && <span style={{ width: 4, height: 4, borderRadius: '50%', background: isSel ? '#fff' : 'var(--color-info)', position: 'absolute', bottom: 4 }} />}
            </div>
          );
        })}
      </div>
    </div>
  );

  return (
    <div className="page-content">
      <div className="page-header">
        <div className="page-header-text">
          <h2>Mis citas</h2>
          <p style={{ textTransform: 'capitalize' }}>{fmtLongDate(selectedDate)}</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-secondary btn-sm" onClick={() => shiftDay(-1)}>‹</button>
          <button className="btn btn-secondary btn-sm" onClick={() => { setSelectedDate(todayStr); setCalYear(new Date().getFullYear()); setCalMonth(new Date().getMonth()); }}>Hoy</button>
          <button className="btn btn-secondary btn-sm" onClick={() => shiftDay(1)}>›</button>
          <button className="btn btn-secondary btn-icon" title="Calendario" onClick={() => setShowCal(s => !s)}>📅</button>
        </div>
      </div>

      {showCal && <div style={{ marginBottom: 16 }}><MiniCalendar /></div>}

      {/* Chips de filtro */}
      <div className="agenda-chips">
        {CHIPS.map(([v, l]) => (
          <button key={v} className={`agenda-chip ${statusFilter === v ? 'active' : ''}`} onClick={() => setStatusFilter(v)}>{l}</button>
        ))}
      </div>

      <div className="agenda-layout">
        {/* Lista */}
        <div>
          {loading ? (
            <div style={{ textAlign: 'center', padding: 40 }}><div className="spinner" style={{ margin: '0 auto' }} /></div>
          ) : dayAppts.length === 0 ? (
            <div className="card" style={{ textAlign: 'center', padding: 40, color: 'var(--color-text-muted)' }}>
              <div style={{ fontSize: 30, marginBottom: 8 }}>📭</div>
              No tienes citas {statusFilter !== 'all' ? 'con este filtro' : 'para este día'}.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {dayAppts.map(a => {
                const color = STATUS_COLOR[a.status] || '#64748b';
                return (
                  <button key={a.id} className="appt-card" onClick={() => { setDetail(a); setCancelMode(false); }}>
                    <div style={{ minWidth: 58, textAlign: 'center' }}>
                      <div style={{ fontWeight: 700, fontSize: '1rem' }}>{a.start_time?.slice(0,5)}</div>
                    </div>
                    <div style={{ width: 4, alignSelf: 'stretch', borderRadius: 4, background: color, flexShrink: 0 }} />
                    <div style={{
                      width: 46, height: 46, borderRadius: '50%', flexShrink: 0,
                      background: avatarColor(a.client_name), color: '#fff',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '0.95rem',
                    }}>{initials(a.client_name)}</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 700 }}>{a.client_name}</div>
                      <div style={{ fontSize: '0.82rem', color: 'var(--color-text-muted)' }}>{a.service_name}</div>
                    </div>
                    <span className={`badge badge-${a.status}`}>{STATUS_LABEL[a.status] || a.status}</span>
                    <span style={{ color: 'var(--color-text-faint)', fontSize: '1.1rem', flexShrink: 0 }}>›</span>
                  </button>
                );
              })}
            </div>
          )}

          {/* Banner informativo */}
          {!loading && allDay.length > 0 && (
            <div style={{ marginTop: 16, display: 'flex', gap: 12, alignItems: 'center', padding: '14px 16px', background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 12, boxShadow: 'var(--shadow-sm)' }}>
              <div style={{ width: 40, height: 40, borderRadius: '50%', background: 'var(--color-info-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>📅</div>
              <div style={{ fontSize: '0.85rem', color: 'var(--color-text-muted)' }}>
                Tienes <strong style={{ color: 'var(--color-text)' }}>{summary.total}</strong> citas programadas para hoy.
                {nextAppt && <><br />Tu próxima cita es a las <strong style={{ color: 'var(--color-text)' }}>{nextAppt.start_time?.slice(0,5)}</strong>.</>}
              </div>
            </div>
          )}
        </div>

        {/* Sidebar (escritorio) */}
        <div className="agenda-side" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <MiniCalendar />
          <div className="card">
            <h4 style={{ marginBottom: 14, fontSize: '0.95rem' }}>Resumen del día</h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {[
                ['📅', summary.total, 'Citas programadas', '#2563eb'],
                ['✅', summary.confirmed, 'Confirmadas', '#16a34a'],
                ['✂️', summary.attended, 'Atendidas', '#8b5cf6'],
                ['✗', summary.cancelled, 'Canceladas', '#dc2626'],
              ].map(([icon, val, label, color]) => (
                <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{ width: 36, height: 36, borderRadius: 8, background: color + '1f', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16 }}>{icon}</div>
                  <div style={{ fontWeight: 700, fontSize: '1.1rem', minWidth: 22 }}>{val}</div>
                  <div style={{ fontSize: '0.85rem', color: 'var(--color-text-muted)' }}>{label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ─── Modal detalle + acciones ─── */}
      {detail && (
        <div className="modal-overlay" onClick={() => { setDetail(null); setCancelMode(false); }}>
          <div className="modal" style={{ maxWidth: 440 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <span className="modal-title">Detalle de la cita</span>
              <button className="btn btn-ghost btn-icon" onClick={() => { setDetail(null); setCancelMode(false); }}>✕</button>
            </div>
            <div className="modal-body">
              {/* Encabezado cliente */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                <div style={{
                  width: 56, height: 56, borderRadius: '50%', background: avatarColor(detail.client_name),
                  color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '1.2rem',
                }}>{initials(detail.client_name)}</div>
                <div>
                  <div style={{ fontWeight: 700, fontSize: '1.05rem' }}>{detail.client_name}</div>
                  <div style={{ fontSize: '0.85rem', color: 'var(--color-text-muted)' }}>{detail.service_name}</div>
                  <span className={`badge badge-${detail.status}`} style={{ marginTop: 4 }}>{STATUS_LABEL[detail.status] || detail.status}</span>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 4 }}>
                <Field label="Hora" value={`${detail.start_time?.slice(0,5)}${detail.end_time ? ' – ' + detail.end_time.slice(0,5) : ''}`} />
                <Field label="Fecha" value={new Date(detail.appointment_date + 'T12:00:00').toLocaleDateString('es-CO')} />
                {detail.client_phone && <Field label="Teléfono" value={detail.client_phone} />}
                {detail.price != null && <Field label="Precio" value={new Intl.NumberFormat('es-CO',{style:'currency',currency:'COP',maximumFractionDigits:0}).format(detail.price)} />}
              </div>

              {/* Modo cancelar */}
              {cancelMode && (
                <div className="form-group" style={{ marginTop: 4 }}>
                  <label className="form-label">Motivo de cancelación *</label>
                  <textarea className="form-textarea" rows={3} value={cancelReason} onChange={e => setCancelReason(e.target.value)} placeholder="Ej: El cliente no se presentó..." />
                </div>
              )}
            </div>

            {/* Acciones */}
            <div className="modal-footer" style={{ flexWrap: 'wrap', justifyContent: 'flex-start' }}>
              {cancelMode ? (
                <>
                  <button className="btn btn-secondary" disabled={updating} onClick={() => setCancelMode(false)}>Volver</button>
                  <button className="btn btn-danger" disabled={updating || !cancelReason.trim()} onClick={() => changeStatus(detail.id, 'cancelled', cancelReason)}>Confirmar cancelación</button>
                </>
              ) : detail.status !== 'cancelled' ? (
                <>
                  {detail.status !== 'attended' && (
                    <button className="btn btn-primary" disabled={updating} onClick={() => changeStatus(detail.id, 'attended')}>Atendida</button>
                  )}
                  {detail.status !== 'not_attended' && (
                    <button className="btn btn-ghost" disabled={updating} onClick={() => changeStatus(detail.id, 'not_attended')}>No asistió</button>
                  )}
                  {detail.status !== 'rescheduled' && (
                    <button className="btn btn-ghost" disabled={updating} onClick={() => changeStatus(detail.id, 'rescheduled')}>Reagendar</button>
                  )}
                  <button className="btn btn-danger" disabled={updating} onClick={() => setCancelMode(true)}>Cancelar</button>
                </>
              ) : (
                <button className="btn btn-secondary" onClick={() => setDetail(null)}>Cerrar</button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <div style={{ fontSize: '0.7rem', color: 'var(--color-text-faint)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>{label}</div>
      <div style={{ fontWeight: 500, fontSize: '0.9rem' }}>{value}</div>
    </div>
  );
}
